import React, { useState, useEffect, useRef } from "react";
import "./style.css";
import { Popper, Paper, ClickAwayListener, Tooltip, Grow } from "@mui/material";

import AddReactionOutlinedIcon from "@mui/icons-material/AddReactionOutlined";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import CelebrationIcon from "@mui/icons-material/Celebration";
import SentimentVerySatisfiedIcon from "@mui/icons-material/SentimentVerySatisfied";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import EmojiPeopleIcon from "@mui/icons-material/EmojiPeople";

const REACTION_CONFIG = [
  { type: "LIKE", Icon: ThumbUpAltIcon, label: "Like", color: "#1976d2" },
  { type: "LOVE", Icon: FavoriteIcon, label: "Love", color: "#e53935" },
  { type: "CLAP", Icon: EmojiPeopleIcon, label: "Clap", color: "#f57c00" },
  {
    type: "PARTY",
    Icon: CelebrationIcon,
    label: "Celebrate",
    color: "#7b1fa2",
  },
  {
    type: "LAUGH",
    Icon: SentimentVerySatisfiedIcon,
    label: "Haha",
    color: "#f9a825",
  },
  { type: "IDEA", Icon: LightbulbIcon, label: "Insightful", color: "#fbc02d" },
  { type: "HOT", Icon: WhatshotIcon, label: "Trending", color: "#e64a19" },
  { type: "APPROVAL", Icon: TaskAltIcon, label: "Approved", color: "#2e7d32" },
  {
    type: "THANKS",
    Icon: VolunteerActivismIcon,
    label: "Thanks",
    color: "#6a1b9a",
  },
  {
    type: "UNLIKE",
    Icon: ThumbDownAltIcon,
    label: "Disagree",
    color: "#616161",
  },
];

const ReactionButtons = ({ update, accessToken }) => {
  const [reactions, setReactions] = useState(update.reactions || []);
  const [userReaction, setUserReaction] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const anchorRef = useRef(null);

  useEffect(() => {
    const found = reactions.find((item) => item.users?.includes(update.userId));
    setUserReaction(found ? found.type : null);
  }, [reactions, update.userId]);

  const handleReactionClick = async (reactionType) => {
    const previousType = userReaction;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/news-feed/reaction/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ feedId: update.id, reactionType }),
        },
      );

      if (!response.ok) throw new Error("Reaction request failed");
      const data = await response.json();

      const updated = [...reactions];

      if (data.toggledOn) {
        if (previousType && previousType !== reactionType) {
          const prevIdx = updated.findIndex((r) => r.type === previousType);
          if (prevIdx !== -1) {
            updated[prevIdx] = {
              ...updated[prevIdx],
              count: updated[prevIdx].count - 1,
            };
            if (updated[prevIdx].count <= 0) updated.splice(prevIdx, 1);
          }
        }

        const idx = updated.findIndex((r) => r.type === reactionType);
        if (idx !== -1) {
          updated[idx] = { ...updated[idx], count: updated[idx].count + 1 };
        } else {
          updated.push({ type: reactionType, count: 1 });
        }

        setUserReaction(reactionType);
      } else {
        const idx = updated.findIndex((r) => r.type === reactionType);
        if (idx !== -1) {
          updated[idx] = { ...updated[idx], count: updated[idx].count - 1 };
          if (updated[idx].count <= 0) updated.splice(idx, 1);
        }
        setUserReaction(null);
      }

      setReactions(updated);
    } catch (err) {
      console.error("Reaction update failed:", err);
    } finally {
      setPickerOpen(false);
    }
  };

  // Build the summary badge show icons for top-3 reaction types + total count
  const activeReactions = reactions.filter((r) => r.count > 0);
  const totalCount = activeReactions.reduce((sum, r) => sum + r.count, 0);

  return (
    <div className="ec-reactions">
      {activeReactions.length > 0 && (
        <div className="ec-reaction-summary">
          {activeReactions.slice(0, 3).map(({ type }) => {
            const cfg = REACTION_CONFIG.find((c) => c.type === type);
            if (!cfg) return null;
            return (
              <Tooltip key={type} title={cfg.label} placement="top" arrow>
                <span className="ec-reaction-icon-chip">
                  <cfg.Icon style={{ color: cfg.color, fontSize: 14 }} />
                </span>
              </Tooltip>
            );
          })}
          <span className="ec-reaction-count">{totalCount}</span>
        </div>
      )}

      <Tooltip title="React" placement="top" arrow>
        <span
          ref={anchorRef}
          className={`ec-add-reaction-btn${pickerOpen ? " open" : ""}`}
          onClick={() => setPickerOpen((prev) => !prev)}
          role="button"
          tabIndex={0}
          aria-haspopup="true"
          aria-expanded={pickerOpen}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") setPickerOpen((p) => !p);
          }}
        >
          <AddReactionOutlinedIcon style={{ fontSize: 18 }} />
        </span>
      </Tooltip>

      <Popper
        open={pickerOpen}
        anchorEl={anchorRef.current}
        placement="top-start"
        transition
        style={{ zIndex: 1500 }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} timeout={150}>
            <Paper elevation={4} className="ec-reaction-picker">
              <ClickAwayListener onClickAway={() => setPickerOpen(false)}>
                <div className="ec-reaction-picker-inner">
                  {REACTION_CONFIG.map(({ type, Icon, color, label }) => {
                    const isActive = userReaction === type;
                    return (
                      <Tooltip key={type} title={label} placement="top" arrow>
                        <button
                          type="button"
                          className={`ec-reaction-option${
                            isActive ? " ec-reaction-active" : ""
                          }`}
                          onClick={() => handleReactionClick(type)}
                          style={{ "--reaction-color": color }}
                          aria-label={`React: ${label}`}
                          aria-pressed={isActive}
                        >
                          <Icon
                            style={{
                              fontSize: 21,
                              color: isActive ? color : undefined,
                            }}
                          />
                        </button>
                      </Tooltip>
                    );
                  })}
                </div>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  );
};

export default ReactionButtons;
